import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Filter, SlidersHorizontal } from 'lucide-react';
import { categories } from '../data/categories';
import { aiTools } from '../data/aiTools';
import { AITool, Category, SubCategory } from '../types';
import { SEOHead } from './SEOHead';
import { ToolCard } from './ToolCard';
import { EmptyState } from './EmptyState';

export const CategoryPage: React.FC = () => {
  const { categoryId, subcategoryId } = useParams<{ categoryId: string; subcategoryId?: string }>();
  const [category, setCategory] = useState<Category | null>(null);
  const [subcategory, setSubcategory] = useState<SubCategory | null>(null);
  const [tools, setTools] = useState<AITool[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<{
    experienceLevels: string[];
    useCases: string[];
    pricingTypes: string[];
  }>({
    experienceLevels: [],
    useCases: [],
    pricingTypes: []
  });
  const [showFilters, setShowFilters] = useState(false);
  const currentMonth = new Date().toLocaleString('default', { month: 'long' });
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    // Reset state when params change
    setIsLoading(true);
    setError(null);
    setShowFilters(false);
    
    // Simulate API fetch with timeout
    const timer = setTimeout(() => {
      try {
        if (!categoryId) {
          setError('Category ID is missing');
          setIsLoading(false);
          return;
        }

        const foundCategory = categories.find(c => c.id === categoryId);
        if (!foundCategory) {
          setError('Category not found');
          setIsLoading(false);
          return;
        }

        setCategory(foundCategory);
        
        if (subcategoryId) {
          const foundSubcategory = foundCategory.subcategories.find(s => s.id === subcategoryId);
          if (!foundSubcategory) {
            setError('Subcategory not found');
            setIsLoading(false);
            return;
          }
          setSubcategory(foundSubcategory);
        } else {
          setSubcategory(null);
        }
        
        // Filter tools by category
        const filteredTools = aiTools.filter(tool => 
          tool.categories.includes(categoryId)
        );
        
        setTools(filteredTools);
        setIsLoading(false);
      } catch (err) {
        setError('Failed to load category data');
        setIsLoading(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [categoryId, subcategoryId]);

  // Apply filters to tools
  const filteredTools = tools.filter(tool => {
    // If no filters are active, show all tools
    if (
      activeFilters.experienceLevels.length === 0 &&
      activeFilters.useCases.length === 0 &&
      activeFilters.pricingTypes.length === 0
    ) {
      return true;
    }
    
    // Check experience levels
    const matchesExperience = activeFilters.experienceLevels.length === 0 || 
      tool.experienceLevels.some(level => activeFilters.experienceLevels.includes(level));
    
    // Check use cases
    const matchesUseCases = activeFilters.useCases.length === 0 || 
      tool.useCases.some(useCase => activeFilters.useCases.includes(useCase));
    
    // Check pricing types
    const matchesPricing = activeFilters.pricingTypes.length === 0 || 
      activeFilters.pricingTypes.includes(tool.pricingType);
    
    return matchesExperience && matchesUseCases && matchesPricing;
  });

  // Toggle a filter value
  const toggleFilter = (type: 'experienceLevels' | 'useCases' | 'pricingTypes', value: string) => {
    setActiveFilters(prev => {
      const current = [...prev[type]];
      const index = current.indexOf(value);
      
      if (index === -1) {
        current.push(value);
      } else {
        current.splice(index, 1);
      }
      
      return {
        ...prev,
        [type]: current
      };
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setActiveFilters({
      experienceLevels: [],
      useCases: [],
      pricingTypes: []
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 p-6 rounded-lg">
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <p className="mb-4">{error || 'Failed to load category data'}</p>
          <Link to="/" className="inline-flex items-center text-red-600 dark:text-red-400 hover:underline">
            <ArrowLeft size={16} className="mr-2" />
            Return to home page
          </Link>
        </div>
      </div>
    );
  }

  // Generate meta title and description
  const metaTitle = subcategory 
    ? `${subcategory.name} AI Tools for ${category.name} | YourAIToolFinder`
    : `${category.name} AI Tools - Reviews & Alternatives | YourAIToolFinder`;
  
  const metaDescription = subcategory
    ? `Discover the best ${subcategory.name.toLowerCase()} AI tools for ${category.name.toLowerCase()}. Compare features, pricing, and alternatives. Updated ${currentMonth} ${currentYear}.`
    : `Find the perfect AI tools for ${category.name.toLowerCase()}. Compare ${category.description.toLowerCase()} with detailed reviews and alternatives. Updated ${currentMonth} ${currentYear}.`;

  return (
    <>
      <SEOHead
        title={metaTitle}
        description={metaDescription}
        ogType="website"
        schemaType="list"
        schemaData={filteredTools}
        breadcrumbs={
          subcategory 
            ? [
                { name: 'Home', url: '/' },
                { name: category.name, url: `/category/${category.id}` },
                { name: subcategory.name, url: `/category/${category.id}/${subcategory.id}` }
              ]
            : [
                { name: 'Home', url: '/' },
                { name: category.name, url: `/category/${category.id}` }
              ]
        }
      />

      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb Navigation */}
        <nav className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-6">
          <Link to="/" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
            Home
          </Link>
          <span className="mx-2">/</span>
          {subcategory ? (
            <>
              <Link 
                to={`/category/${category.id}`} 
                className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                {category.name}
              </Link>
              <span className="mx-2">/</span>
              <span className="text-gray-900 dark:text-gray-100 font-medium">
                {subcategory.name}
              </span>
            </>
          ) : (
            <span className="text-gray-900 dark:text-gray-100 font-medium">
              {category.name}
            </span>
          )}
        </nav>

        {/* Back Button */}
        <Link 
          to={subcategory ? `/category/${category.id}` : '/'} 
          className="inline-flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 mb-6"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to {subcategory ? category.name : 'all categories'}
        </Link>

        {/* Category Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700 mb-8">
          <div className="p-6 md:p-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              {subcategory ? subcategory.name : category.name} AI Tools
            </h1>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-4">
              {subcategory ? subcategory.description : category.description}
            </p>
            
            {!subcategory && category.subcategories.length > 0 && (
              <div className="mt-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Browse by Subcategory
                </h2>
                <div className="flex flex-wrap gap-3">
                  {category.subcategories.map(sub => (
                    <Link
                      key={sub.id}
                      to={`/category/${category.id}/${sub.id}`}
                      className="px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-800/30 text-indigo-700 dark:text-indigo-300 rounded-lg text-sm font-medium transition-colors"
                    >
                      {sub.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filters and Results */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters - Desktop */}
          <div className="hidden lg:block">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700 sticky top-24">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Filters</h2>
                  {(activeFilters.experienceLevels.length > 0 || 
                    activeFilters.useCases.length > 0 || 
                    activeFilters.pricingTypes.length > 0) && (
                    <button 
                      onClick={clearFilters}
                      className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                {/* Experience Level Filter */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Experience Level</h3>
                  <div className="space-y-2">
                    {['beginner', 'intermediate', 'advanced'].map(level => (
                      <label key={level} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={activeFilters.experienceLevels.includes(level)}
                          onChange={() => toggleFilter('experienceLevels', level)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                          {level}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Use Cases Filter */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Use Cases</h3>
                  <div className="space-y-2">
                    {['writing', 'images', 'video', 'audio', 'data-analysis', 'automation', 'research', 'coding'].map(useCase => (
                      <label key={useCase} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={activeFilters.useCases.includes(useCase)}
                          onChange={() => toggleFilter('useCases', useCase)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                          {useCase.replace('-', ' ')}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Pricing Filter */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Pricing</h3>
                  <div className="space-y-2">
                    {[
                      { id: 'free', label: 'Free' },
                      { id: 'freemium', label: 'Freemium' },
                      { id: 'paid', label: 'Paid' }
                    ].map(pricing => (
                      <label key={pricing.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={activeFilters.pricingTypes.includes(pricing.id)}
                          onChange={() => toggleFilter('pricingTypes', pricing.id)}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          {pricing.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Results */}
          <div className="lg:col-span-3">
            {/* Mobile Filter Button */}
            <div className="lg:hidden mb-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="w-full flex items-center justify-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm"
              >
                <Filter size={16} className="mr-2 text-gray-500 dark:text-gray-400" />
                <span className="text-gray-700 dark:text-gray-300">Filters</span>
                {(activeFilters.experienceLevels.length > 0 || 
                  activeFilters.useCases.length > 0 || 
                  activeFilters.pricingTypes.length > 0) && (
                  <span className="ml-2 px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300 rounded-full text-xs">
                    {activeFilters.experienceLevels.length + activeFilters.useCases.length + activeFilters.pricingTypes.length}
                  </span>
                )}
              </button>
              
              {/* Mobile Filters Panel */}
              {showFilters && (
                <div className="mt-4 bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Filters</h2>
                    {(activeFilters.experienceLevels.length > 0 || 
                      activeFilters.useCases.length > 0 || 
                      activeFilters.pricingTypes.length > 0) && (
                      <button 
                        onClick={clearFilters}
                        className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  
                  {/* Experience Level Filter */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Experience Level</h3>
                    <div className="flex flex-wrap gap-2">
                      {['beginner', 'intermediate', 'advanced'].map(level => (
                        <button
                          key={level}
                          onClick={() => toggleFilter('experienceLevels', level)}
                          className={`px-3 py-1 rounded-full text-sm ${
                            activeFilters.experienceLevels.includes(level)
                              ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          } capitalize`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Use Cases Filter */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Use Cases</h3>
                    <div className="flex flex-wrap gap-2">
                      {['writing', 'images', 'video', 'audio', 'data-analysis', 'automation', 'research', 'coding'].map(useCase => (
                        <button
                          key={useCase}
                          onClick={() => toggleFilter('useCases', useCase)}
                          className={`px-3 py-1 rounded-full text-sm ${
                            activeFilters.useCases.includes(useCase)
                              ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          } capitalize`}
                        >
                          {useCase.replace('-', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Pricing Filter */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Pricing</h3>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: 'free', label: 'Free' },
                        { id: 'freemium', label: 'Freemium' },
                        { id: 'paid', label: 'Paid' }
                      ].map(pricing => (
                        <button
                          key={pricing.id}
                          onClick={() => toggleFilter('pricingTypes', pricing.id)}
                          className={`px-3 py-1 rounded-full text-sm ${
                            activeFilters.pricingTypes.includes(pricing.id)
                              ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {pricing.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => setShowFilters(false)}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Results Count */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {filteredTools.length} {filteredTools.length === 1 ? 'Tool' : 'Tools'} Found
              </h2>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <SlidersHorizontal size={16} className="mr-2" />
                <span>Sort by: </span>
                <select className="ml-2 bg-transparent border-none focus:ring-0 text-gray-700 dark:text-gray-300 font-medium">
                  <option>Relevance</option>
                  <option>Rating: High to Low</option>
                  <option>Rating: Low to High</option>
                </select>
              </div>
            </div>
            
            {/* Results Grid */}
            {filteredTools.length > 0 ? (
              <div className="space-y-6">
                {filteredTools.map((tool, index) => (
                  <motion.div
                    key={tool.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <ToolCard tool={tool} index={index} />
                  </motion.div>
                ))}
              </div>
            ) : (
              <EmptyState message="No tools match your current filters. Try adjusting your filter criteria or browse a different category." />
            )}
          </div>
        </div>
      </div>
    </>
  );
};